"use client";

import Image from "next/image";
import { motion } from "motion/react";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AboutFeature } from "@/data/about";

interface AboutFeatureCardProps {
  card: AboutFeature;
}

function AvatarStack({ avatars }: { avatars: string[] }) {
  return (
    <div className="flex items-center -space-x-2">
      {avatars.map((avatar) => (
        <div
          key={avatar}
          className="relative h-9 w-9 overflow-hidden rounded-full border-[1.5px] border-[#111216]"
        >
          <Image
            src={avatar}
            alt="Artisan avatar"
            fill
            sizes="36px"
            className="object-cover"
          />
        </div>
      ))}
    </div>
  );
}

export function AboutFeatureCard({ card }: AboutFeatureCardProps) {
  const dark = card.tone === "dark";

  return (
    <div data-about-reveal className="w-full h-full">
      <motion.article
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.2 }}
        className={cn(
          "relative flex flex-col justify-between p-8 h-full min-h-[320px] rounded-[36px]",
          dark
            ? "bg-[#111216] text-white"
            : "bg-[#a69d91] text-white"
        )}
      >
        {/* Card Header */}
        <div className="flex items-start justify-between w-full">
          {dark && card.avatars ? (
            <AvatarStack avatars={card.avatars} />
          ) : (
            <span className="rounded-full border border-white/40 px-4 py-1 text-[12px] font-light tracking-wide">
              {card.label}
            </span>
          )}

          <ArrowUpRight className="h-5 w-5 text-white/90 stroke-[1.5]" />
        </div>

        {/* Card Body */}
        <div className="mt-8 flex flex-col gap-3">
          <h3 className="text-[28px] font-normal leading-[1.12] tracking-tight max-w-[220px]">
            {card.title}
          </h3>

          <p
            className={cn(
              "text-[15px] leading-relaxed max-w-[270px] mt-2",
              dark ? "text-white/70" : "text-white/85"
            )}
          >
            {card.description}
          </p>
        </div>
      </motion.article>
    </div>
  );
}